/**
 * Country filter for the Target Investors list (FR-21a).
 *
 * Options come from canonical country names only: raw stored locations fold
 * through normaliseToInvestorCountry (AU -> Australia) so one country can
 * never split into raw-seed duplicates (the FR-13 admin-directory bug).
 * Leftover values that fail normalisation stay selectable as "(legacy)".
 *
 * Phase 7B adds multi-select state. The string matcher stays for a single
 * key (including Not set). Inclusive All shows every row.
 */
import type { ClientRaiseTargetInvestor } from '../types/targetInvestor.types';
import { getEffectiveTargetLocation } from './locationMixDisplay';
import { normaliseToInvestorCountry } from './locationUtils';
import {
  TARGET_INVESTOR_FIELD_FILTER_ALL,
  TARGET_INVESTOR_FIELD_FILTER_NOT_SET,
} from './targetInvestorFields';

export interface TargetCountryFilterOption {
  value: string;
  label: string;
}

export type TargetCountryFilterState = {
  all: boolean;
  selectedKeys: string[];
};

export type AllCountriesCheckState = 'checked' | 'unchecked' | 'indeterminate';

export const buildTargetCountryFilterOptions = (
  investors: ClientRaiseTargetInvestor[]
): TargetCountryFilterOption[] => {
  const canonical = new Set<string>();
  const legacy = new Set<string>();
  investors.forEach((investor) => {
    const effective = getEffectiveTargetLocation(investor);
    if (!effective) return;
    const normalised = normaliseToInvestorCountry(effective);
    if (normalised) {
      canonical.add(normalised);
    } else {
      legacy.add(effective);
    }
  });
  const byName = (a: string, b: string) => a.localeCompare(b);
  return [
    ...Array.from(canonical)
      .sort(byName)
      .map((value) => ({ value, label: value })),
    ...Array.from(legacy)
      .sort(byName)
      .map((value) => ({ value, label: `${value} (legacy)` })),
  ];
};

export const getCountryFilterOptionValues = (
  options: TargetCountryFilterOption[]
): string[] => [TARGET_INVESTOR_FIELD_FILTER_NOT_SET, ...options.map((option) => option.value)];

export const createAllCountryFilter = (
  optionValues: readonly string[]
): TargetCountryFilterState => ({
  all: true,
  selectedKeys: [...optionValues],
});

const sameKeyList = (left: readonly string[], right: readonly string[]): boolean => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

export const reconcileCountryFilter = (
  state: TargetCountryFilterState,
  optionValues: readonly string[]
): TargetCountryFilterState => {
  if (state.all) {
    const next = createAllCountryFilter(optionValues);
    if (sameKeyList(state.selectedKeys, next.selectedKeys) && state.all) return state;
    return next;
  }
  const allowed = new Set(optionValues);
  const selectedKeys = optionValues.filter((value) => {
    return allowed.has(value) && state.selectedKeys.includes(value);
  });
  const all = optionValues.length > 0 && selectedKeys.length === optionValues.length;
  if (all === state.all && sameKeyList(state.selectedKeys, selectedKeys)) return state;
  return { all, selectedKeys };
};

export const getAllCountriesCheckState = (
  state: TargetCountryFilterState
): AllCountriesCheckState => {
  if (state.all) return 'checked';
  if (state.selectedKeys.length === 0) return 'unchecked';
  return 'indeterminate';
};

export const isInclusiveAllCountryFilter = (state: TargetCountryFilterState): boolean =>
  state.all;

export const isCountryFilterActive = (state: TargetCountryFilterState): boolean => !state.all;

export const toggleAllCountries = (
  state: TargetCountryFilterState,
  optionValues: readonly string[]
): TargetCountryFilterState => {
  if (getAllCountriesCheckState(state) === 'checked') {
    return { all: false, selectedKeys: [] };
  }
  return createAllCountryFilter(optionValues);
};

export const toggleCountryFilterValue = (
  state: TargetCountryFilterState,
  value: string,
  optionValues: readonly string[]
): TargetCountryFilterState => {
  if (!optionValues.includes(value)) return state;
  const selected = new Set(state.all ? optionValues : state.selectedKeys);
  if (selected.has(value)) {
    selected.delete(value);
  } else {
    selected.add(value);
  }
  const selectedKeys = optionValues.filter((key) => selected.has(key));
  return {
    all: optionValues.length > 0 && selectedKeys.length === optionValues.length,
    selectedKeys,
  };
};

export const getCountryFilterTriggerLabel = (
  state: TargetCountryFilterState,
  options: TargetCountryFilterOption[],
  idleLabel = 'Country'
): string => {
  if (state.all || state.selectedKeys.length === 0) return idleLabel;
  if (state.selectedKeys.length === 1) {
    const key = state.selectedKeys[0];
    if (key === TARGET_INVESTOR_FIELD_FILTER_NOT_SET) return 'Not set';
    return options.find((option) => option.value === key)?.label ?? key;
  }
  return `${state.selectedKeys.length} countries`;
};

export const matchesCountryRawValue = (
  raw: string | null | undefined,
  filter: string
): boolean => {
  if (!filter || filter === TARGET_INVESTOR_FIELD_FILTER_ALL) return true;
  const effective = typeof raw === 'string' ? raw.trim() : '';
  if (filter === TARGET_INVESTOR_FIELD_FILTER_NOT_SET) return !effective;
  if (!effective) return false;
  const normalised = normaliseToInvestorCountry(effective);
  return normalised ? normalised === filter : effective === filter;
};

export const matchesCountryFilterSelection = (
  raw: string | null | undefined,
  filter: string | TargetCountryFilterState
): boolean => {
  if (typeof filter !== 'string') {
    if (filter.all) return true;
    if (filter.selectedKeys.length === 0) return false;
    return filter.selectedKeys.some((key) => matchesCountryRawValue(raw, key));
  }
  return matchesCountryRawValue(raw, filter);
};

const matchesCountryFilterKey = (
  investor: ClientRaiseTargetInvestor,
  filter: string
): boolean => {
  if (!filter || filter === TARGET_INVESTOR_FIELD_FILTER_ALL) return true;
  const effective = getEffectiveTargetLocation(investor);
  if (filter === TARGET_INVESTOR_FIELD_FILTER_NOT_SET) return !effective;
  if (!effective) return false;
  const normalised = normaliseToInvestorCountry(effective);
  // Canonical selections match every raw variant (AU/au/Australia); legacy
  // options carry the raw string, so they match exactly.
  return normalised ? normalised === filter : effective === filter;
};

export const matchesTargetCountryFilter = (
  investor: ClientRaiseTargetInvestor,
  filter: string | TargetCountryFilterState
): boolean => {
  if (typeof filter !== 'string') {
    if (filter.all) return true;
    if (filter.selectedKeys.length === 0) return false;
    return filter.selectedKeys.some((key) => matchesCountryFilterKey(investor, key));
  }
  return matchesCountryFilterKey(investor, filter);
};
